import { heydayPath, navigateToHeydayPage, pageFromPathname, subscribeToHeydayLocation } from '../../config/heydayRoutes';
import type {
  TenantSettingsRootPage,
  TenantSettingsRoutePort,
  TenantSettingsView,
} from '../../features/tenant-settings/public';

const roots = new Set<TenantSettingsRootPage>(['admin-company-info', 'admin-affiliates', 'admin-company-seals', 'admin-reference-data', 'admin-appearance']);

function defaultView(root: TenantSettingsRootPage): TenantSettingsView {
  if (root === 'admin-affiliates') return 'companies';
  if (root === 'admin-company-seals') return 'company-seals';
  if (root === 'admin-reference-data') return 'reference-data';
  if (root === 'admin-appearance') return 'organization-brand';
  return 'company-info';
}

const parsedViewsByRoot: Readonly<Record<TenantSettingsRootPage, Readonly<Record<string, TenantSettingsView>>>> = {
  'admin-company-info': { name: 'organization-name' },
  'admin-affiliates': { legal: 'company-legal' },
  'admin-company-seals': { seal: 'company-seal' },
  'admin-appearance': {},
  'admin-reference-data': {
    locations: 'locations',
    'work-spaces': 'work-spaces',
    'job-families': 'job-families',
    'job-profiles': 'job-profiles',
    'job-levels': 'job-levels',
    'job-types': 'job-types',
    'employee-number': 'employee-number',
  },
};

const urlViews: Readonly<Partial<Record<TenantSettingsView, string>>> = {
  'organization-name': 'name', 'company-legal': 'legal', 'company-seal': 'seal',
  locations: 'locations', 'work-spaces': 'work-spaces', 'job-families': 'job-families', 'job-profiles': 'job-profiles',
  'job-levels': 'job-levels', 'job-types': 'job-types', 'employee-number': 'employee-number',
};

function search(root: TenantSettingsRootPage, view: TenantSettingsView, companyId = '') {
  const parameters = new URLSearchParams();
  if (view !== defaultView(root) && urlViews[view]) parameters.set('view', urlViews[view]!);
  if ((view === 'company-legal' || view === 'company-seal') && companyId) parameters.set('company', companyId);
  return parameters;
}

export const tenantSettingsRouteAdapter: TenantSettingsRoutePort = {
  read(preferredRoot) {
    const page = pageFromPathname(window.location.pathname);
    if (!page || !roots.has(page as TenantSettingsRootPage)) {
      return {
        root: preferredRoot,
        view: defaultView(preferredRoot),
        companyId: '',
        canonical: true,
      };
    }
    const requestedRoot = page as TenantSettingsRootPage;
    const parameters = new URLSearchParams(window.location.search);
    const requested = parameters.get('view') ?? '';
    let view = parsedViewsByRoot[requestedRoot][requested] ?? defaultView(requestedRoot);
    const root = requestedRoot;
    let companyId = parameters.get('company') ?? '';
    if ((view === 'company-legal' || view === 'company-seal') && !companyId) {
      view = defaultView(root);
      companyId = '';
    }
    const canonicalPath = heydayPath(root, search(root, view, companyId));
    return { root, view, companyId, canonical: `${window.location.pathname}${window.location.search}` === canonicalPath };
  },
  navigate(root, view, companyId = '', options) {
    navigateToHeydayPage(root, search(root, view, companyId), options);
  },
  subscribe: subscribeToHeydayLocation,
};
